import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";

import API_Calls_Template from "@/api/calls/API_Calls_Template";
import { ProcessingDuration } from "@/components/membership/Preview/summaryComponents";
import SummaryTabs from "@/components/membership/Preview/SummaryTabs";
import CatalistOnlyChip from "@/components/sharedComponents/CatalistOnlyChip";
import ErrorFallback from "@/components/sharedComponents/ErrorFallback";
import useTemplateNamesHandler from "@/hooks/useTemplateNamesHandler";
import { canEditIEB } from "@/utils/permissionsHandler";
import { formatLargeNumber } from "@/utils/util";

export default function SummaryBody({
  isModal = false,
  summaryData,
  fileData,
  IEBApproved = false,
  handleIEBChange = null,
}) {
  const [mapText, setMapText] = useState(null);
  const { getDisplayName } = useTemplateNamesHandler();
  const { getRawTemplate } = API_Calls_Template();
  const [isIEB, setIsIEB] = useState(IEBApproved);
  const isInitialMount = useRef(true);
  const { user } = useAuth0();
  const summary = summaryData.summary;

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (isModal) {
      handleIEBChange(
        summaryData.processedFileAffiliate,
        summaryData.processTimestamp,
        isIEB
      );
    }
    // handleIEBChange is recreated by the parent on every render; depending
    // on it would cause infinite loops. summaryData fields are stable while
    // mounted but pulled out as-is so the comment explicitly excludes them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIEB]);

  const handleIEBClick = (e) => {
    setIsIEB(e.target.checked);
    handleIEBChange(
      fileData.affiliate,
      fileData.process_date,
      e.target.checked
    );
  };

  useEffect(() => {
    fetchRawTemplate();
    // fetchRawTemplate/getRawTemplate are recreated each render; intentionally
    // fire only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRawTemplate = async () => {
    const res = await getRawTemplate(
      fileData.affiliate,
      fileData.template_name,
      fileData.template_date
    );
    if (!res.error && res.data) {
      setMapText(res.data);
    } else {
      setMapText(res);
      console.error(
        "Error retrieving template: ",
        res.message || "Unknown error"
      );
    }
  };

  return (
    <Box data-testid="summary-body">
      <ErrorBoundary
        fallbackRender={({ error }) => (
          <ErrorFallback error={error} redirect="/" />
        )}
      >
        {/* FILE DATA */}
        {fileData && (
          <Box
            sx={{
              marginBottom: "20px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography>
                Data file:{" "}
                <span style={{ fontStyle: "italic" }}>
                  {fileData.filename} {fileData.upload_date}
                </span>
              </Typography>

              <Typography>
                Template:{" "}
                <span style={{ fontStyle: "italic" }}>
                  {getDisplayName(
                    fileData.template_name,
                    fileData.template_date
                  )}{" "}
                </span>{" "}
                {summary.use === "catalist-only" && <CatalistOnlyChip />}
              </Typography>

              {isModal && (
                <Typography>Processed: {fileData.process_date}</Typography>
              )}

              <Typography>
                Uploaded Rows: {formatLargeNumber(summary.member_rows)}
              </Typography>

              {!isModal && (
                <Box>
                  <Typography>
                    <ProcessingDuration
                      duration={summaryData.processedDuration}
                    />
                  </Typography>
                  <Typography>
                    Dropped duplicate records: {summaryData.droppedRows}
                  </Typography>
                </Box>
              )}

              {/* if persons key doesn't exist, or if the value is 0 (meaning local_key is not mapped) do not render */}
              {summary.persons ? (
                <Typography>
                  Unique persons: {formatLargeNumber(summary.persons)}
                </Typography>
              ) : null}
            </Box>

            <Box>
              {summary.confirmed && summary.use !== "catalist-only" && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isIEB}
                          onChange={handleIEBClick}
                          disabled={!canEditIEB(user)}
                        />
                      }
                      label="IEB approved"
                    />
                  </FormGroup>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box>
          <SummaryTabs summary={summary} mapText={mapText} />
        </Box>
      </ErrorBoundary>
    </Box>
  );
}
